import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Divider, Stack, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { transformarCotizacionAPedido } from '../../utils/cotizacionToPedido';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import clienteService from '../../services/clienteService';
import ubicacionService from '../../services/ubicacionService';
import {
  VentaPendienteMaster,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import PDFViewer from '../../components/shared/PDFViewer';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { parseLocalDate, toLocalDateStringYMD } from '../../utils/formatters';

type PedidoVentaUI = PedidoVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type PedidoVentaUIExt = PedidoVentaUI & { nroSuc?: number; cuit?: string; tipoIVA?: string; idCompania?: string };

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
const DEFAULT_NRO_SUC = 1;
/** Pedido nuevo: 0 para que FormularioVentaForm asigne la primera sucursal de la lista cargada. */
const NRO_SUC_PEDIDO_NUEVO = 0;
const IVA_ALICUOTA = 0.21;

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number =>
  Math.round(l.cantidad * l.precioUnitario * (1 - l.descuento / 100) * 100) / 100;

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  return !strValue || strValue === '0' ? '' : strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  return FORMAS_DE_PAGO_VALIDAS.find(f => f.toUpperCase() === cleaned) || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) return 0;
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
};

const lineasDesdeDetalles = (detalles: PedidoVentaDetalle[]): POSLinea[] =>
  detalles.map((d) => ({
    id: genId(),
    codigoArticulo: d.codigoArticulo,
    descripcionArticulo: d.descripcionArticulo || '',
    cantidad: d.cantidad,
    precioUnitario: d.precioUnitario,
    descuento: (d as any).descuento ?? (d as any).porcDesc ?? 0,
    subtotal: d.subtotal || 0,
    um: (d as any).um || '',
    notas: (d as any).notas,
    stockDisponible: (d as any).stockDisponible,
    nroFormularioMaster: (d as any).nroFormularioMaster,
    nroItemMaster: (d as any).nroItemMaster,
  } as POSLinea & { nroFormularioMaster?: number; nroItemMaster?: number }));

const clienteDesdeData = (data: any): POSCliente => ({
  nroCliente: data.nroCliente,
  nombre: data.nombre,
  cuit: data.cuit || '',
  tipoIva: data.tipoIVA || data.tipoIva || '',
  nroLista: data.nroLista || '',
  condVta: data.formaDePago || data.condVta || '',
});

const pedidoVentaInicial: PedidoVentaUIExt = {
  nroFormulario: 0,
  nroTipoFormulario: '12',
  tipoCliente: 'C',
  fechaEmision: new Date(),
  horaEmision: new Date(),
  nroCliente: 0,
  nombre: '',
  nroVendedor: 0,
  nroLista: '',
  codigoMoneda: 'ARS',
  importeTotal: 0,
  asentado: 'N',
  descripcion: '',
  usuario: '',
  ordenCompra: '',
  transporte: '',
  ubicacion: '',
  formaDePago: '',
  nroSuc: NRO_SUC_PEDIDO_NUEVO,
  cuit: '',
  tipoIVA: '',
  idCompania: '',
  detalles: [],
  porcDesc: 0,
  porcDesc2: 0,
  porcDesc3: 0,
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const PedidosVenta: React.FC = () => {
  const navigate = useNavigate();
  const { pedidoId } = useParams<{ pedidoId?: string }>();
  const [searchParams] = useSearchParams();
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const cotizacionId = cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : null;
  const location = useLocation();

  const modo = pedidoId
    ? (location.pathname.includes('/ver') ? 'ver' : 'editar')
    : 'crear';
  const isReadOnly = modo === 'ver';
  const isEditMode = modo === 'editar';

  /** Pedido nuevo cargado desde ?cotizacionId= (botón Preparar cotización) */
  const preparandoDesdeCotizacion = Boolean(cotizacionId && !isNaN(cotizacionId)) && !pedidoId;

  const [loading, setLoading] = useState(false);
  const [loadingPedido, setLoadingPedido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedidoVenta, setPedidoVenta] = useState<PedidoVentaUIExt>(pedidoVentaInicial);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);
  const [infoCotizacion, setInfoCotizacion] = useState<{ letra?: string; puntoVenta?: number; nroComprobante?: number; nroFormulario?: number } | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroPedidoCreado, setNroPedidoCreado] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [pendingApplied, setPendingApplied] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const ventaPendiente = (location.state as { ventaPendiente?: VentaPendienteMaster } | null)?.ventaPendiente;

  // Resetear infoCotizacion cuando no hay cotizacionId
  useEffect(() => {
    if (!cotizacionId || isNaN(cotizacionId)) setInfoCotizacion(null);
  }, [cotizacionId]);

  // Venta pendiente → agregar línea POS
  useEffect(() => {
    if (pendingApplied || !ventaPendiente || pedidoId || (cotizacionId && !isNaN(cotizacionId))) return;

    setLineasPOS((prev) => {
      const alreadyIncluded = prev.some(
        (l: any) =>
          l.nroFormularioMaster === ventaPendiente.nroFormularioMaster &&
          l.nroItemMaster === ventaPendiente.nroItemMaster,
      );
      if (alreadyIncluded) return prev;

      const cantidad = cantidadSugeridaDesdePendienteVenta(ventaPendiente);
      const precioLista = Number(ventaPendiente.precioListaMaster ?? ventaPendiente.precioUnitarioMaster ?? 0) || 0;
      const porcDescDet = Number(ventaPendiente.porcDesc1Master ?? 0) || 0;
      const subtotal = round2(cantidad * (precioLista - (precioLista * porcDescDet) / 100));

      return [
        ...prev,
        {
          id: genId(),
          codigoArticulo: ventaPendiente.codigoArticulo || '',
          descripcionArticulo: ventaPendiente.descripcionArticulo || '',
          cantidad,
          precioUnitario: precioLista,
          descuento: porcDescDet,
          subtotal,
          um: '',
          nroFormularioMaster: ventaPendiente.nroFormularioMaster,
          nroItemMaster: ventaPendiente.nroItemMaster,
        } as any,
      ];
    });

    if (ventaPendiente.nroCliente && ventaPendiente.nroCliente > 0) {
      setPedidoVenta((prev) => ({
        ...prev,
        nroCliente: ventaPendiente.nroCliente!,
        nombre: ventaPendiente.nombreCliente || prev.nombre,
      }));
    }

    setPendingApplied(true);
  }, [pendingApplied, ventaPendiente, pedidoId, cotizacionId]);

  // Cargar cotización para pre-cargar pedido (modo crear con ?cotizacionId=)
  useEffect(() => {
    if (cotizacionId && !isNaN(cotizacionId) && !pedidoId) {
      const cargarDesdeCotizacion = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionId);
          const cotizacionExt = cotizacion as any;

          setInfoCotizacion({
            letra: cotizacionExt.letra || '',
            puntoVenta: cotizacionExt.nroSuc ?? 0,
            nroComprobante: cotizacionExt.nroComprobante ?? 0,
            nroFormulario: cotizacion.nroFormulario,
          });

          let ubicacionFormateada = cotizacionExt.ubicacion || '';
          const nroUbicacionOrigen = cotizacionExt.nroUbicacionOrigen || cotizacionExt.nroUbicacion;
          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const encontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = encontrada
                ? `${encontrada.nombreUbicacion} (${encontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = cotizacionExt.transporte || '';
          const nroExpreso = cotizacionExt.nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const pedidoTransformado = transformarCotizacionAPedido(cotizacion);
          const detallesConSubtotales = pedidoTransformado.detalles?.map((d) => ({
            ...d,
            precioUnitario: (d as any).precioLista || d.precioUnitario,
            descuento: (d as any).porcDesc ?? d.descuento,
          })) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((s, d) => s + (d.subtotal || 0), 0);
          const fechaEmisionDate = pedidoTransformado.fechaEmision
            ? (pedidoTransformado.fechaEmision instanceof Date ? pedidoTransformado.fechaEmision : new Date(pedidoTransformado.fechaEmision))
            : new Date();
          const horaEmisionDate = pedidoTransformado.horaEmision
            ? (pedidoTransformado.horaEmision instanceof Date ? pedidoTransformado.horaEmision : new Date(pedidoTransformado.horaEmision))
            : new Date();

          const pedidoCargado: PedidoVentaUIExt = {
            ...pedidoTransformado,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: cotizacionExt.ordenDeCompra || cotizacionExt.ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: cotizacionExt.formaDePago || '',
            nroSuc:
              cotizacionExt.nroSuc != null && Number(cotizacionExt.nroSuc) > 0
                ? Number(cotizacionExt.nroSuc)
                : NRO_SUC_PEDIDO_NUEVO,
            cuit: cotizacionExt.cuit || '',
            tipoIVA: cotizacionExt.tipoIVA || cotizacionExt.tipoIva || '',
            idCompania: cotizacionExt.idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: pedidoTransformado.importeTotal && pedidoTransformado.importeTotal > 0
              ? pedidoTransformado.importeTotal
              : round2(importeTotalCalculado),
          } as PedidoVentaUIExt;

          setPedidoVenta(pedidoCargado);
          setLineasPOS(lineasDesdeDetalles(detallesConSubtotales));
          setPosCliente(clienteDesdeData(pedidoCargado));
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar la cotización';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        } finally {
          setLoadingPedido(false);
        }
      };
      cargarDesdeCotizacion();
      return;
    }

    if (pedidoId) {
      const cargarPedido = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const id = parseInt(pedidoId, 10);
          if (isNaN(id)) {
            setError('ID de pedido inválido');
            navigate('/ventas/pedidos');
            return;
          }

          const pedidoCargado = await pedidoVentaService.getPedidoVenta(id);

          let ubicacionFormateada = (pedidoCargado as any).ubicacion || '';
          const nroUbicacionOrigen =
            (pedidoCargado as any).nroUbicacionOrigen ||
            (pedidoCargado as any).nroUbicacion ||
            (pedidoCargado as any).formulario?.nroUbicacionOrigen;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const encontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = encontrada
                ? `${encontrada.nombreUbicacion} (${encontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (pedidoCargado as any).transporte || '';
          const nroExpreso = (pedidoCargado as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = pedidoCargado.detalles?.map((detalle) => {
            const precioLista = (detalle as any).precioLista || detalle.precioUnitario || 0;
            const porcDesc = (detalle as any).porcDesc || 0;
            return { ...detalle, precioUnitario: precioLista, descuento: porcDesc, subtotal: detalle.subtotal || 0 };
          }) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((s, d) => s + (d.subtotal || 0), 0);
          const fechaEmisionDate = pedidoCargado.fechaEmision
            ? (pedidoCargado.fechaEmision instanceof Date ? pedidoCargado.fechaEmision : new Date(pedidoCargado.fechaEmision))
            : new Date();
          const horaEmisionDate = pedidoCargado.horaEmision
            ? (pedidoCargado.horaEmision instanceof Date ? pedidoCargado.horaEmision : new Date(pedidoCargado.horaEmision))
            : new Date();

          const nroVendedorCargado = Number((pedidoCargado as any).nroVendedor ?? (pedidoCargado as any).NroVendedor ?? 0);

          const pedidoActualizado: PedidoVentaUIExt = {
            ...pedidoCargado,
            fechaEmision: fechaEmisionDate,
            horaEmision: horaEmisionDate,
            ordenCompra: (pedidoCargado as any).ordenCompra || (pedidoCargado as any).ordenDeCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (pedidoCargado as any).formaDePago || '',
            nroSuc: (pedidoCargado as any).nroSuc || DEFAULT_NRO_SUC,
            cuit: (pedidoCargado as any).cuit || '',
            tipoIVA: (pedidoCargado as any).tipoIVA || (pedidoCargado as any).tipoIva || '',
            idCompania: (pedidoCargado as any).idCompania || '',
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
            detalles: detallesConSubtotales,
            importeTotal: pedidoCargado.importeTotal && pedidoCargado.importeTotal > 0
              ? pedidoCargado.importeTotal
              : round2(importeTotalCalculado),
          } as PedidoVentaUIExt;

          setPedidoVenta(pedidoActualizado);
          setLineasPOS(lineasDesdeDetalles(detallesConSubtotales));
          setPosCliente(clienteDesdeData(pedidoActualizado));
        } catch (err: any) {
          const message = err?.response?.data || 'Error al cargar el pedido';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/pedidos'), 2000);
        } finally {
          setLoadingPedido(false);
        }
      };
      cargarPedido();
    }
  }, [pedidoId, cotizacionId, navigate, enqueueSnackbar]);

  // Persistir sucursal en localStorage
  useEffect(() => {
    const v = Number(pedidoVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [pedidoVenta.nroSuc]);

  // Debounce: al cambiar nroCliente, enriquecer datos del cliente
  useEffect(() => {
    if (isEditMode || isReadOnly || preparandoDesdeCotizacion) return undefined;
    const value = pedidoVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro);
            setPosCliente(c as unknown as POSCliente);
            setPedidoVenta((prev) => {
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                nroVendedor: Number((c as any).vendedor) > 0 ? Number((c as any).vendedor) : 0,
                nroLista: obtenerCodigoListaCliente(c as any),
                formaDePago: obtenerFormaPagoSugerida(c as any),
                transporte: obtenerTransporteSugerido(c as any),
                porcDesc: normalizarPorcentajeBonificacion(c.porcDesc ?? (c as any)?.porc_Desc),
                porcDesc2: normalizarPorcentajeBonificacion(c.porcDesc2 ?? (c as any)?.porc_Desc2),
                porcDesc3: normalizarPorcentajeBonificacion(c.porcDesc3 ?? (c as any)?.porc_Desc3),
              } as PedidoVentaUIExt;
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [pedidoVenta.nroCliente, isEditMode, isReadOnly, preparandoDesdeCotizacion]);

  // Sincronizar lineasPOS → pedidoVenta.detalles (en modos crear y editar)
  useEffect(() => {
    if (modo === 'ver') return;
    const detalles = lineasPOS.map((l, idx): PedidoVentaDetalle => ({
      nroIdformulario: pedidoVenta.nroFormulario,
      nroItem: idx + 1,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      descuento: l.descuento,
      porcDesc: l.descuento,
      subtotal: l.subtotal,
      notas: l.notas,
      um: l.um,
      nroFormularioMaster: (l as any).nroFormularioMaster,
      nroItemMaster: (l as any).nroItemMaster,
    }));
    const importeTotal = detalles.reduce((acc, d) => acc + (d.subtotal || 0), 0);
    setPedidoVenta((prev) => ({ ...prev, detalles, importeTotal: round2(importeTotal) }));
  }, [lineasPOS, modo, pedidoVenta.nroFormulario]);

  // ── Handlers cliente ──────────────────────────────────────────
  const handleClienteSelect = (cliente: any) => {
    setPosCliente(cliente as POSCliente);
    const nroClienteSel = cliente.nroCliente ?? cliente.codigo ?? 0;
    setPedidoVenta((prev) => ({
      ...prev,
      nroCliente: nroClienteSel,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      nroVendedor:
        preparandoDesdeCotizacion && nroClienteSel === prev.nroCliente && Number(prev.nroVendedor) > 0
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      nroLista:
        preparandoDesdeCotizacion && nroClienteSel === prev.nroCliente && String(prev.nroLista || '').trim() !== ''
          ? String(prev.nroLista || '').trim()
          : obtenerCodigoListaCliente(cliente),
      formaDePago: obtenerFormaPagoSugerida(cliente),
      transporte: obtenerTransporteSugerido(cliente),
      porcDesc: normalizarPorcentajeBonificacion(cliente.porcDesc ?? (cliente as any)?.porc_Desc),
      porcDesc2: normalizarPorcentajeBonificacion(cliente.porcDesc2 ?? (cliente as any)?.porc_Desc2),
      porcDesc3: normalizarPorcentajeBonificacion(cliente.porcDesc3 ?? (cliente as any)?.porc_Desc3),
    }));
  };

  // ── Handlers líneas POS ───────────────────────────────────────
  const handleAgregarProductoPOS = (item: SuggestionItem) => {
    setLineasPOS((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: item.precioUnitario,
        um: item.um,
        stockDisponible: item.stockDisponible,
      },
    ]);
  };

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => setLineasPOS((prev) => prev.filter((l) => l.id !== id));

  const handleDuplicarLinea = (id: string) => {
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setLineasPOS((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '' },
    ]);
  };

  const handlePorcDescChange = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setPedidoVenta((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  // ── Totales ───────────────────────────────────────────────────
  const subtotalNeto = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1 = (subtotalNeto * (pedidoVenta.porcDesc || 0)) / 100;
  const baseDesc2 = subtotalNeto - importeDesc1;
  const importeDesc2 = (baseDesc2 * (pedidoVenta.porcDesc2 || 0)) / 100;
  const baseDesc3 = baseDesc2 - importeDesc2;
  const importeDesc3 = (baseDesc3 * (pedidoVenta.porcDesc3 || 0)) / 100;
  const subtotalConDesc = baseDesc3 - importeDesc3;
  const iva = Math.round(subtotalConDesc * IVA_ALICUOTA * 100) / 100;
  const total = subtotalConDesc + iva;

  // ── Guardar ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar un pedido en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0)
        erroresValidacion.push('Debe seleccionar un cliente');
      if (!pedidoVenta.fechaEmision)
        erroresValidacion.push('Debe ingresar la Fecha de Emisión');
      if (!(pedidoVenta as PedidoVentaUI).ubicacion?.trim())
        erroresValidacion.push('Debe seleccionar una Ubicación');
      if (!(pedidoVenta as PedidoVentaUI).formaDePago?.trim())
        erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!pedidoVenta.detalles || pedidoVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto al pedido');
      } else {
        pedidoVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0)
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          if (!detalle.codigoArticulo)
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const payload = {
        ...pedidoVenta,
        nroSuc: pedidoVenta.nroSuc || DEFAULT_NRO_SUC,
        ordenCompra: pedidoVenta.ordenCompra || '',
        transporte: pedidoVenta.transporte || '',
        ubicacion: pedidoVenta.ubicacion || '',
        formaDePago: pedidoVenta.formaDePago || '',
        nroFormularioCotizacion: (!isEditMode && cotizacionId && !isNaN(cotizacionId)) ? cotizacionId : undefined,
      } as any;

      let nroPedido: number | null = null;

      if (isEditMode && pedidoId) {
        const id = parseInt(pedidoId, 10);
        await pedidoVentaService.updatePedidoVenta(id, payload);
        nroPedido = id;
        enqueueSnackbar('Pedido actualizado exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/pedidos'), 1500);
        setLoading(false);
        return;
      }

      const response = await pedidoVentaService.createPedidoVenta(payload);
      if (response?.nroFormulario) nroPedido = response.nroFormulario;
      else if ((response as any)?.formulario?.nroFormulario) nroPedido = (response as any).formulario.nroFormulario;
      else if ((response as any)?.data?.nroFormulario) nroPedido = (response as any).data.nroFormulario;

      if (nroPedido) {
        setNroPedidoCreado(nroPedido);
        setSuccessDialogOpen(true);
        try {
          setLoadingPDF(true);
          const blob = await pedidoVentaService.getPedidoVentaPDF(nroPedido);
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
          setTimeout(() => setSuccessDialogOpen(false), 1500);
          enqueueSnackbar(`PDF del pedido #${nroPedido} generado correctamente`, { variant: 'success', autoHideDuration: 3000 });
        } catch {
          enqueueSnackbar('Error al generar el PDF del pedido', { variant: 'warning' });
        } finally {
          setLoadingPDF(false);
        }
        setPedidoVenta(pedidoVentaInicial);
        setPosCliente(null);
        setLineasPOS([]);
      } else {
        enqueueSnackbar('Pedido de venta guardado exitosamente', { variant: 'success' });
        setPedidoVenta(pedidoVentaInicial);
        setPosCliente(null);
        setLineasPOS([]);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar el pedido de venta';

      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);
        errores.forEach((e: string) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 6000 }));
        errorMessage = err.message;
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          msgs.push(Array.isArray(val) ? `${k}: ${val.join(' - ')}` : `${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
          errorMessage = msgs.join('\n');
        }
      } else if (status === 400 || status === 500) {
        errorMessage = data?.message || errorMessage;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      } else {
        const msg = (data?.message || data || err?.message || '').toString();
        if (msg) {
          errorMessage = msg.length > 200 ? msg.substring(0, 200) + '...' : msg;
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setNroPedidoCreado(null);
    navigate('/ventas/pedidos');
  };

  const nroLista = obtenerCodigoListaCliente(posCliente) || undefined;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/ventas/pedidos'))}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Pedidos
        </Button>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="h5" fontWeight={700} color="primary.main">
          {isReadOnly
            ? `Ver Pedido #${pedidoId}`
            : isEditMode
              ? `Editar Pedido #${pedidoId}`
              : 'Nuevo Pedido de Venta'}
        </Typography>
        <Chip
          label={isReadOnly ? 'SOLO LECTURA' : isEditMode ? 'EDICIÓN' : 'NUEVO'}
          size="small"
          color={isReadOnly ? 'default' : 'primary'}
          variant="outlined"
        />
        {!isReadOnly && cotizacionId && infoCotizacion && (
          <Chip
            label={
              infoCotizacion.letra && (infoCotizacion.puntoVenta ?? 0) > 0 && (infoCotizacion.nroComprobante ?? 0) > 0
                ? `Desde cotización ${infoCotizacion.letra}-${String(infoCotizacion.puntoVenta ?? 0).padStart(5, '0')}-${String(infoCotizacion.nroComprobante ?? 0).padStart(8, '0')} (#${infoCotizacion.nroFormulario})`
                : `Desde cotización #${infoCotizacion.nroFormulario || cotizacionId}`
            }
            color="info"
            size="small"
          />
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loadingPedido ? (
        <Box sx={{ p: 2, textAlign: 'center' }}><Typography>Cargando pedido...</Typography></Box>
      ) : (
        <Grid container spacing={2} alignItems="flex-start">
          {/* Columna principal */}
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Cliente</Typography>
                <FacturaPOSClientePanel
                  cliente={posCliente}
                  onClienteSelect={handleClienteSelect}
                  readOnly={isReadOnly}
                />
              </Box>
              {!isReadOnly && (
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Agregar artículo</Typography>
                  <FacturaPOSSearchBar
                    nroLista={nroLista}
                    disabled={!posCliente}
                    onAgregarProducto={handleAgregarProductoPOS}
                  />
                </Box>
              )}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                  <Typography variant="overline" color="text.secondary">Artículos</Typography>
                  {lineasPOS.length > 0 && (
                    <Chip label={`${lineasPOS.length} renglón${lineasPOS.length > 1 ? 'es' : ''}`} size="small" />
                  )}
                </Stack>
                <FacturaPOSLineasGrid
                  lineas={lineasPOS}
                  nroLista={nroLista}
                  onLineaChange={handleLineaChange}
                  onLineaFilled={handleLineaFilled}
                  onEliminarLinea={handleEliminarLinea}
                  onDuplicarLinea={handleDuplicarLinea}
                  onAgregarLineaVacia={handleAgregarLineaVacia}
                  readOnly={isReadOnly}
                />
              </Box>
            </Stack>
          </Grid>

          {/* Panel lateral */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <FacturaPOSTotalesPanel
                subtotalNeto={subtotalNeto}
                porcDesc={pedidoVenta.porcDesc || 0}
                porcDesc2={pedidoVenta.porcDesc2 || 0}
                porcDesc3={pedidoVenta.porcDesc3 || 0}
                onPorcDescChange={handlePorcDescChange}
                iva={iva}
                total={total}
                observaciones={pedidoVenta.descripcion ?? ''}
                onObservacionesChange={(v) =>
                  setPedidoVenta((prev) => ({ ...prev, descripcion: v }))
                }
                cliente={posCliente}
                fechaEmision={toLocalDateStringYMD(
                  pedidoVenta.fechaEmision instanceof Date
                    ? pedidoVenta.fechaEmision
                    : new Date(pedidoVenta.fechaEmision as string | number),
                )}
                onFechaEmisionChange={(ymd) =>
                  setPedidoVenta((prev) => ({ ...prev, fechaEmision: parseLocalDate(ymd) }))
                }
                nroVendedor={Number(pedidoVenta.nroVendedor) || 0}
                onNroVendedorChange={(nro) =>
                  setPedidoVenta((prev) => ({ ...prev, nroVendedor: nro }))
                }
                formaDePago={pedidoVenta.formaDePago || ''}
                onFormaDePagoChange={(v) => setPedidoVenta((prev) => ({ ...prev, formaDePago: v }))}
                ordenCompra={pedidoVenta.ordenCompra || ''}
                onOrdenCompraChange={(v) => setPedidoVenta((prev) => ({ ...prev, ordenCompra: v }))}
                transporte={pedidoVenta.transporte || ''}
                onTransporteChange={(v) => setPedidoVenta((prev) => ({ ...prev, transporte: v }))}
                ubicacion={pedidoVenta.ubicacion || ''}
                onUbicacionChange={(v) => setPedidoVenta((prev) => ({ ...prev, ubicacion: v }))}
                saving={loading}
                onGuardar={() => handleSave()}
                cantLineas={lineasPOS.length}
                readOnly={isReadOnly}
                hideDraftButton
                saveLabel={isEditMode ? 'Actualizar Pedido' : 'Guardar Pedido'}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Diálogo de éxito */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>Pedido Creado</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Pedido creado exitosamente! Número de Pedido: {nroPedidoCreado}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={() => setSuccessDialogOpen(false)} variant="text" color="primary" disabled={loadingPDF} sx={{ textTransform: 'none' }}>
            {loadingPDF ? 'Generando PDF...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && nroPedidoCreado && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Pedido #${nroPedidoCreado}`}
          documentId={`${nroPedidoCreado}`}
          documentType="pedido"
          downloadFileName={`Pedido_${nroPedidoCreado}.pdf`}
        />
      )}
    </Box>
  );
};

export default PedidosVenta;
